import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MapIcon from '@mui/icons-material/Map';
import { Chip, IconButton, Stack, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { TruckDriverIdAndName } from 'src/entities/orderEntity';
import { PlaceEntity } from 'src/entities/placeEntity';
import { dataGridUtil } from 'src/utils/dataGrid.util';
import { BerthReservationStatusLabels } from 'src/vo/BerthReservationStatus.vo';
import { LocationRoleLabels } from 'src/vo/LocationRole.vo';

export const Columns: (
  openEditDialog?: (placeEntity: PlaceEntity) => void,
  openMapDialog?: (placeEntity: PlaceEntity) => void,
) => GridColDef[] = (
  openEditDialog,
  openMapDialog
) => {
  const getWidth = (name: string, width: number) => {
    const maybeWidth: number = dataGridUtil.getSavedColumnWidth('place-columns', name);

    return maybeWidth || width;
  };

  const getSpecifiedTimeValue = (
    row: PlaceEntity,
    index: number,
    type: 'start' | 'end'
  ) => {
    const times = row.place_specified_times_attributes;
    if (times?.length >= index + 1) {
      const relativeDay = type === 'start' ? times[index].start_relative_day : times[index].end_relative_day;
      const time = type === 'start' ? times[index].start_at : times[index].end_at;
      const prefix = `(D${relativeDay > 0 ? '+' : ''}${Math.abs(relativeDay)})`;
      return `${prefix} ${time}`;
    }
    return '';
  };

  return [
    {
      field: 'edit',
      headerName: '編集',
      disableExport: true,
      renderCell: (params: { row: PlaceEntity }) => (
        <IconButton onClick={() => openEditDialog(params.row)}>
          <EditOutlinedIcon />
        </IconButton>
      ),
      filterable: false,
      sortable: false,
    },
    {
      field: 'encoded_id',
      headerName: 'ID',
      minWidth: 0,
      maxWidth: 0,
      filterable: false,
      sortable: false,
    },
    {
      field: 'new_from_order',
      headerName: 'NEW',
      valueGetter: (_, row: PlaceEntity) => (row.new_from_order ? 'NEW' : ''),
      renderCell: (params: { row: PlaceEntity }) => {
        if (params.row.new_from_order) return <Chip size="small" color="error" label="NEW" />;
        return '';
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'company_name',
      headerName: '事業所',
      width: getWidth('company_name', 300),
      filterable: false,
      sortable: false,
    },
    {
      field: 'internal_location_code',
      headerName: '地点コード',
      width: getWidth('internal_location_code', 140),
    },
    {
      field: 'name',
      headerName: '名前',
      width: getWidth('name', 300),
    },
    {
      field: 'address',
      headerName: '住所',
      width: getWidth('address', 400),
    },
    {
      field: 'location_role',
      headerName: '拠点区分',
      width: getWidth('location_role', 100),
      valueFormatter: (value: string) => {
        if (!value) return '';
        return LocationRoleLabels[value as keyof typeof LocationRoleLabels] ?? '';
      },
      renderCell: (params: { row: PlaceEntity }) => {
        if (!params.row.location_role) return '';
        return LocationRoleLabels[params.row.location_role] ?? '';
      },
      type: 'singleSelect',
      valueOptions: [
        { value: 'loading', label: '積地' },
        { value: 'unloading', label: '降地' },
        { value: 'both', label: '両方' },
      ],
    },
    {
      field: 'berth_reservation_status',
      headerName: 'バース予約',
      width: getWidth('berth_reservation_status', 100),
      valueFormatter: (value: string) => {
        if (!value) return '';
        return BerthReservationStatusLabels[value as keyof typeof BerthReservationStatusLabels] ?? '';
      },
      renderCell: (params: { row: PlaceEntity }) => {
        if (!params.row.berth_reservation_status) return '';
        return BerthReservationStatusLabels[params.row.berth_reservation_status] ?? '';
      },
      type: 'singleSelect',
      valueOptions: [
        { value: 'required', label: '要' },
        { value: 'not_required', label: '不要' },
        { value: 'unknown', label: '未確認' },
      ],
    },
    {
      field: 'is_main_warehouse',
      headerName: '主要倉庫',
      width: getWidth('is_main_warehouse', 90),
      renderCell: (params: { row: PlaceEntity }) => {
        if (params.row.is_main_warehouse) {
          return (
            <Stack sx={{ height: '100%' }} alignContent="center" alignItems="center">
              <Tooltip title="主要倉庫" sx={{ height: '100%' }}>
                <Chip
                  size="medium"
                  label="✔"
                  sx={{
                    backgroundColor: 'transparent',
                    height: '100%',
                  }}
                />
              </Tooltip>
            </Stack>
          );
        }
        return '';
      },
    },
    {
      field: 'position',
      headerName: '座標',
      disableExport: true,
      width: getWidth('position', 250),
      valueGetter: (_, row: PlaceEntity) => {
        if (row.latitude && row.longitude) {
          return `${row.latitude}, ${row.longitude}`;
        }
        return '';
      },
      renderCell: (params: { row: PlaceEntity }) => {
        if (params.row.latitude && params.row.longitude) {
          return (
            <Stack direction="row" alignContent="center" alignItems="center">
              <Tooltip title="位置を調整します">
                <IconButton onClick={() => openMapDialog(params.row)}>
                  <MapIcon />
                </IconButton>
              </Tooltip>
              {[params.row.latitude, params.row.longitude].join(', ')}
            </Stack>
          );
        }
        return '';
      }
    },
    {
      field: 'latitude',
      headerName: '緯度',
      filterable: false,
      sortable: false,
    },
    {
      field: 'longitude',
      headerName: '経度',
      filterable: false,
      sortable: false,
    },
    {
      field: 'specified_time_start_at_1',
      headerName: '受付',
      width: getWidth('specified_time_start_at_1', 100),
      valueGetter: (_, row: PlaceEntity) => getSpecifiedTimeValue(row, 0, 'start'),
      filterable: false,
      sortable: false,
    },
    {
      field: 'specified_time_end_at_1',
      headerName: '締切',
      width: getWidth('specified_time_end_at_1', 100),
      valueGetter: (_, row: PlaceEntity) => getSpecifiedTimeValue(row, 0, 'end'),
      filterable: false,
      sortable: false,
    },
    {
      field: 'specified_time_start_at_2',
      headerName: '受付②',
      width: getWidth('specified_time_start_at_2', 100),
      valueGetter: (_, row: PlaceEntity) => getSpecifiedTimeValue(row, 1, 'start'),
      filterable: false,
      sortable: false,
    },
    {
      field: 'specified_time_end_at_2',
      headerName: '締切②',
      width: getWidth('specified_time_end_at_2', 100),
      valueGetter: (_, row: PlaceEntity) => getSpecifiedTimeValue(row, 1, 'end'),
      filterable: false,
      sortable: false,
    },
    {
      field: 'specified_time_start_at_3',
      headerName: '受付③',
      width: getWidth('specified_time_start_at_3', 100),
      valueGetter: (_, row: PlaceEntity) => getSpecifiedTimeValue(row, 2, 'start'),
      filterable: false,
      sortable: false,
    },
    {
      field: 'specified_time_end_at_3',
      headerName: '締切③',
      width: getWidth('specified_time_end_at_3', 100),
      valueGetter: (_, row: PlaceEntity) => getSpecifiedTimeValue(row, 2, 'end'),
      filterable: false,
      sortable: false,
    },
    {
      field: 'staying_minutes',
      headerName: '滞在時間',
      type: 'number' as const,
      width: getWidth('staing_minutes', 100),
    },
    {
      field: 'designated_truck_klasses',
      headerName: '車両タイプ',
      width: getWidth('designated_truck_klasses', 80),
      valueFormatter: (value: string[]) => {
        if (!value || value.length === 0 || value.length === 1) { // CSVに出力する場合
          return value;
        }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity}) => { // 画面に表示する場合
        if (!params.row.designated_truck_klasses) { return ''; }
        return params.row.designated_truck_klasses.join(',');
      }
    },
    {
      field: 'designated_truck_car_models',
      headerName: '車種',
      width: getWidth('designated_truck_car_models', 80),
      valueFormatter: (value: string[]) => {
        if (!value || value.length === 0 || value.length === 1) { // CSVに出力する場合
          return value;
        }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity}) => { // 画面に表示する場合
        if (!params.row.designated_truck_car_models) { return ''; }
        return params.row.designated_truck_car_models.join(',');
      }
    },
    {
      field: 'designated_truck_loading_platform_heights',
      headerName: '荷台高',
      width: getWidth('designated_truck_loading_platform_heights', 80),
      valueFormatter: (value: string[]) => {
        if (!value || value.length === 0 || value.length === 1) { // CSVに出力する場合
          return value;
        }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity}) => { // 画面に表示する場合
        if (!params.row.designated_truck_loading_platform_heights) { return ''; }
        return params.row.designated_truck_loading_platform_heights.join(',');
      }
    },
    {
      field: 'designated_truck_loading_platform_widths',
      headerName: '荷台幅',
      width: getWidth('designated_truck_loading_platform_widths', 80),
      valueFormatter: (value: string[]) => {
        if (!value || value.length === 0 || value.length === 1) { // CSVに出力する場合
          return value;
        }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity}) => { // 画面に表示する場合
        if (!params.row.designated_truck_loading_platform_widths) { return ''; }
        return params.row.designated_truck_loading_platform_widths.join(',');
      }
    },
    {
      field: 'designated_truck_loading_platform_lengths',
      headerName: '荷台長',
      width: getWidth('designated_truck_loading_platform_lengths', 80),
      valueFormatter: (value: string[]) => {
        if (!value || value.length === 0 || value.length === 1) { // CSVに出力する場合
          return value;
        }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity}) => { // 画面に表示する場合
        if (!params.row.designated_truck_loading_platform_lengths) { return ''; }
        return params.row.designated_truck_loading_platform_lengths.join(',');
      }
    },
    {
      field: 'designated_truck_floor_specifications',
      headerName: '床仕様',
      width: getWidth('designated_truck_floor_specifications', 80),
      valueFormatter: (value: string[]) => {
        if (!value || value.length === 0 || value.length === 1) { // CSVに出力する場合
          return value;
        }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity}) => { // 画面に表示する場合
        if (!params.row.designated_truck_floor_specifications) { return ''; }
        return params.row.designated_truck_floor_specifications.join(',');
      }
    },
    {
      field: 'designated_truck_features',
      headerName: '装置',
      width: getWidth('designated_truck_features', 80),
      valueFormatter: (value: string[]) => {
        if (!value || value.length === 0 || value.length === 1) { // CSVに出力する場合
          return value;
        }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity}) => { // 画面に表示する場合
        if (!params.row.designated_truck_features) { return ''; }
        return params.row.designated_truck_features.join(',');
      }
    },
    { field: 'shipper_name', headerName: '荷主名', width: getWidth('shipper_name', 100), },
    { field: 'shipper_contact_person', headerName: '担当者', width: getWidth('shipper_contact_person', 100) },
    { field: 'shipper_phone_number', headerName: '電話番号', width: getWidth('shipper_phone_number', 100) },
    { field: 'shipper_email_address', headerName: 'メールアドレス', width: getWidth('shipper_email_address', 100), },
    {
      field: 'allowed_trucks',
      headerName: '指定トラック',
      width: 200,
      valueFormatter: (value: TruckDriverIdAndName[]) => { // CSVに出力する場合
        if (!value) { return ''; }
        if (value.length < 2) { return value.map((obj) => obj.name).join(','); }
        return `"${value.map((obj) => obj.name).join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity }) => { // 画面に表示する場合
        if (!params.row.allowed_trucks) { return ''; }
        return params.row.allowed_trucks.map((obj) => obj.name).join(',');
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'denied_drivers',
      headerName: 'NGドライバー',
      width: 200,
      valueFormatter: (value: TruckDriverIdAndName[]) => { // CSVに出力する場合
        if (!value) { return ''; }
        if (value.length < 2) { return value.map((obj) => obj.name).join(','); }
        return `"${value.map((obj) => obj.name).join(',')}"`;
      },
      renderCell: (params: { row: PlaceEntity }) => { // 画面に表示する場合
        if (!params.row.denied_drivers) { return ''; }
        return params.row.denied_drivers.map((obj) => obj.name).join(',');
      },
      filterable: false,
      sortable: false,
    },
  ];
};
